// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { defineMessages } from "react-intl";
import { Tree, TreeNode } from "react-organizational-chart";

import { IAction } from "actions";
import { isSet } from "shared/utils/utils";
import { getEnergySourceName, roundTimestamp } from "guiutils";
import { ITrendDataRow } from "states/trendingstates";

import TariffCircuitListModel from "model/tariffcircuitlist";
import ChartBaseComponent, {
  IChartBaseDispatch,
  IChartBaseExtProps,
  IChartBaseProps,
  IChartBaseState,
  IChartTariffNode,
  mapStateToProps as baseMapStateToProps,
  mapDispatchToProps as baseMapDispatchToProps,
  ITrendTimestamps,
} from "./ChartBase";
import { IState } from "states/states";
import { HierarchySelectionState, ITreeViewDropDownOption } from "./TreeViewDropDown";
import { TimePeriod } from "types";
import IconZoomIn from "icons/iconZoomIn";
import IconZoomOut from "icons/iconZoomOut";
import IconResetView from "icons/iconZoomReset";
import { Button } from "react-bootstrap";
import cssContent from "constants.module.scss";

interface IHierarchyChartExtProps extends IChartBaseExtProps {}

interface IHierarchyChartProps extends IChartBaseProps {}

interface IHierarchyChartDispatch extends IChartBaseDispatch {}

interface IHierarchyChartState extends IChartBaseState {
  value: number;
  additionalTCs?: number[];
}

interface IHierarchyTreeNode {
  tariffCircuit: IChartTariffNode;
  childCircuits: IHierarchyTreeNode[];
}

const mapStateToProps = (state: IState, ownProps: IChartBaseExtProps) => {
  const props = {
    ...baseMapStateToProps(state, ownProps),
  };
  return props;
};

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IHierarchyChartDispatch = {
    ...baseMapDispatchToProps(dispatch),
  };
  return dispatchProps;
};

const messages = defineMessages({
  zoomIn: {
    id: "Zoom in",
  },
  zoomOut: {
    id: "Zoom out",
  },
  resetView: {
    id: "Reset view",
  },
});

class HierarchyChartComponent extends ChartBaseComponent<
  IHierarchyChartProps & IHierarchyChartExtProps & IHierarchyChartDispatch,
  IHierarchyChartState
> {
  // enable/disable gray-out of items based on hierarchy selection
  private readonly isHierarchyGrayOutEnabled = false;

  public state = { value: 100, additionalTCs: undefined } as IHierarchyChartState;

  private additionalTCs: number[] = [];

  // gets the whole subtree of all item indexes with given parent
  private getTcTree(
    allTcs: ITreeViewDropDownOption[],
    curTC: ITreeViewDropDownOption,
    trendTimestamps: ITrendTimestamps,
    additionalTCs: number[],
    trendDataIndex: Map<string, ITrendDataRow>,
  ): IHierarchyTreeNode {
    // ensure suitable recursion level; overflow may happen only in illegal parameter set
    if (isSet(curTC.referenceEntry)) {
      // gets all item indexes with given parent
      const children = allTcs.filter(
        (aTC) => aTC.referenceEntry?.parent === curTC.referenceEntry?.id,
      );

      return {
        tariffCircuit: this.makeChartTariffNode(
          curTC!,
          trendTimestamps,
          false,
          additionalTCs,
          trendDataIndex,
        ),
        childCircuits: children.map((curTar) =>
          this.getTcTree(allTcs, curTar, trendTimestamps, additionalTCs, trendDataIndex),
        ),
      };
    } else {
      // energy-source node is selected!
      // create dummy-node and add all items without parents to this node!
      const children = allTcs.filter(
        (aTC) => isSet(aTC.referenceEntry) && !isSet(aTC.referenceEntry?.parent),
      );

      return {
        tariffCircuit: this.makeChartEnergySourceNode(curTC!),
        childCircuits: children.map((curTar) =>
          this.getTcTree(allTcs, curTar, trendTimestamps, additionalTCs, trendDataIndex),
        ),
      };
    }
  }

  private prepareChartData() {
    const trendTimestamps = this.getTimestampsForTrendData(
      this.props.trendData,
      this.props.startDate,
      this.props.endDate,
      this.props.timezoneOffset,
      this.props.timePeriod ?? TimePeriod.CUSTOM,
    );
    const chartData: IHierarchyTreeNode[][] = [];

    const selectedTcs =
      this.props.isHierarchyViewMode ?
        this.allTariffCircuits.filter((aTC) => {
          return (
            aTC.checked === HierarchySelectionState.SELECTED ||
            aTC.checked === HierarchySelectionState.SELECTED_CHILD
          );
        })
      : this.allTariffCircuits;

    const rootTC = selectedTcs[0];
    const rootTcs = isSet(rootTC) ? [rootTC] : [];

    const additionalTCs =
      this.state.additionalTCs ??
      (this.props.isHierarchyViewMode === true ?
        this.additionalTCs
      : this.getAllTarifIDsToOpenSelectedTariff());

    // Pre-index trendData once for O(1) lookups in makeChartTariffNode
    const trendDataIndex = new Map<string, ITrendDataRow>();
    this.props.trendData?.forEach((row) => {
      trendDataIndex.set(`${row.tlId}:${roundTimestamp(row.timestamp)}`, row);
    });

    // get hierarchical tree of all root nodes
    const rootNodeTrees = rootTcs.map((curRoot) =>
      this.getTcTree(
        this.allTariffCircuits,
        curRoot,
        trendTimestamps,
        additionalTCs,
        trendDataIndex,
      ),
    );

    chartData.push(rootNodeTrees);

    return chartData;
  }

  private getAllTarifIDsToOpenSelectedTariff() {
    const allSelectedTariffs = this.allTariffCircuits
      .filter((tc) => isSet(tc.referenceEntry) && tc.checked === HierarchySelectionState.SELECTED)
      .map((tc) => tc.referenceEntry!.id ?? tc.entryId.at(-1) ?? -1);
    const allParentIds: number[] = [];

    allSelectedTariffs.forEach((tc) => {
      allParentIds.push(...this.getParentIds(this.allTariffCircuits, tc));
    });

    this.additionalTCs = Array.from(new Set(allParentIds));

    return this.additionalTCs;
  }

  private renderTree() {
    const chartData = this.prepareChartData();

    return (
      <>
        {chartData.map((anEnergyNodeInfo, eIndex) => (
          <React.Fragment key={`tariffNode_${eIndex}`}>
            {chartData.length > 1 && anEnergyNodeInfo.length > 0 && (
              <h2 className="sourceheading">
                {getEnergySourceName(this.props.selectedEnergySource)}
              </h2>
            )}
            <div className="slidecontainer d-flex justify-content-center mb-3">
              <Button
                className="me-2"
                variant="secondary"
                title={this.context.formatMessage(messages.zoomOut)}
                onClick={() => this.setState({ value: Math.max(this.state.value - 10, 0) })}
              >
                <IconZoomOut width={"24px"} height={"24px"} />
              </Button>
              <Button
                variant="secondary"
                title={this.context.formatMessage(messages.zoomIn)}
                onClick={() => this.setState({ value: Math.min(this.state.value + 10, 100) })}
              >
                <IconZoomIn width={"24px"} height={"24px"} />
              </Button>
              <Button
                className="ms-4"
                variant="secondary"
                title={this.context.formatMessage(messages.resetView)}
                onClick={() => this.setState({ value: 100, additionalTCs: undefined })}
              >
                <IconResetView width={"24px"} height={"24px"} />
              </Button>
            </div>
            {anEnergyNodeInfo.map((aNodeInfo, index) => {
              // render root node
              return (
                <div
                  key={`TreeRootDiv_${eIndex}_${index}`}
                  className={`hierarchyTree mt-0 mb-3 zoom-${this.state.value}`}
                >
                  <Tree
                    lineBorderRadius="10px"
                    lineColor="lightgray"
                    lineHeight={aNodeInfo.childCircuits.length > 0 ? "30px" : "0px"}
                    lineStyle="solid"
                    label={
                      isSet(aNodeInfo.tariffCircuit.parentId) ?
                        this.renderStyledMoreDiv(aNodeInfo, true)
                      : this.renderStyledDiv(aNodeInfo, true)
                    }
                  >
                    {aNodeInfo.childCircuits.length > 0 ?
                      //
                      isSet(aNodeInfo.tariffCircuit.parentId) ?
                        this.renderTreeNode(aNodeInfo, index)
                      : aNodeInfo.childCircuits.map((aNode) => this.renderTreeNode(aNode, index))
                    : <></>}
                  </Tree>
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </>
    );
  }

  private renderTreeNode(aTariff: IHierarchyTreeNode, index: number) {
    return (
      <TreeNode key={`TreeNode_${aTariff.tariffCircuit.id}`} label={this.renderStyledDiv(aTariff)}>
        {(
          aTariff.tariffCircuit.hasChildren === true &&
          aTariff.childCircuits.some((tc) => tc.tariffCircuit.isVisible) === false
        ) ?
          <TreeNode
            key={`TreeNode_${aTariff.tariffCircuit.id}_more`}
            label={this.renderStyledMoreDiv(aTariff)}
          ></TreeNode>
        : aTariff.childCircuits.map((aNode) => this.renderTreeNode(aNode, index))}
      </TreeNode>
    );
  }

  private renderStyledDiv(inTariff: IHierarchyTreeNode, inIsRootNode = false) {
    const clickable =
      inIsRootNode === false &&
      ((this.props.selection?.isHierarchyViewMode === false &&
        inTariff.tariffCircuit.parentId !== undefined &&
        this.isHierarchyGrayOutEnabled) ||
        (inTariff.tariffCircuit.hasChildren === true &&
          (inTariff.tariffCircuit.isDisabled === false ||
            inTariff.tariffCircuit.isVisible === true)));

    const isHighlighted =
      !isSet(this.props.filterText) ||
      this.props.filterText === "" ||
      inTariff.tariffCircuit.name
        .toLocaleLowerCase()
        .includes(this.props.filterText.toLocaleLowerCase());

    return (
      <div
        style={{
          background: `${
            (
              ((inTariff.tariffCircuit.isVisible && inTariff.tariffCircuit.isDisabled === false) ||
                !this.isHierarchyGrayOutEnabled) &&
              isHighlighted
            ) ?
              inTariff.tariffCircuit.color
            : cssContent.GRAPH_GRAYED_OUT
          }80`,
          border: `1px solid ${inTariff.tariffCircuit.color}80`,
        }}
        className={`hierarchyTreeNode user-select-none ${clickable ? "link" : ""} ${
          inTariff.tariffCircuit.isDisabled && this.isHierarchyGrayOutEnabled ? "grayout" : ""
        }`}
        onClick={(event) => {
          if (!clickable) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }

          if (event.metaKey === false && event.ctrlKey === false) {
            // add/remove children from additionalTCs

            // get children of this node and add them to additionalTCs
            const childIds =
              event.shiftKey ?
                this.openChilds(inTariff)
              : inTariff.childCircuits.map((child) => child.tariffCircuit.id);

            const additionalTCs = [
              ...(this.state.additionalTCs ?? this.additionalTCs).filter(
                (aTC) => !childIds.includes(aTC),
              ),
              ...(inTariff.childCircuits[0]?.tariffCircuit.isVisible === false ? childIds : []),
            ];

            this.setState({
              additionalTCs,
            });

            // do not change any other selection - so leave here
            return;
          } else if (this.props.isHierarchyViewMode) {
            this.setState({
              additionalTCs: [
                ...(this.state.additionalTCs ?? this.additionalTCs).filter(
                  (aTC) => aTC !== inTariff.tariffCircuit.id,
                ),
                inTariff.tariffCircuit.id,
              ],
            });
          }
          // in all other cases -> update now the seletion as well...

          const newSelectedTcIds = () => {
            if (this.props.isHierarchyViewMode === true) {
              const tcModel = new TariffCircuitListModel(
                this.props.tariffCircuits,
                this.props.energyMeters,
              );

              return this.prepareNextLevel(tcModel, inTariff.tariffCircuit.id);
            } else {
              const selectedList = this.allTariffCircuits
                .filter(
                  (tc) =>
                    (tc.checked === HierarchySelectionState.SELECTED ||
                      tc.checked === HierarchySelectionState.SELECTED_CHILD) &&
                    inTariff.tariffCircuit.id !== tc.referenceEntry?.id,
                )
                .map((tc) => tc.referenceEntry?.id as number);

              if (inTariff.tariffCircuit.isDisabled === true) {
                selectedList.push(inTariff.tariffCircuit.id);
              }
              return selectedList;
            }
          };

          this.props.onHierarchyChanged(newSelectedTcIds(), this.props.isHierarchyViewMode ?? true);
        }}
        title={inTariff.tariffCircuit.tooltip ?? ""}
      >
        <div>
          <div>{inTariff.tariffCircuit.name}</div>
          <div className={"treeNodeValue"}>{inTariff.tariffCircuit.valueText}</div>
        </div>
      </div>
    );
  }

  private renderStyledMoreDiv(inTariff: IHierarchyTreeNode, inIsRoot = false) {
    const clickable =
      inTariff.tariffCircuit.hasChildren === true ||
      (inTariff.tariffCircuit.isDisabled === false && this.isHierarchyGrayOutEnabled);
    return (
      <div
        style={{
          background: "white",
          border: "1px dashed gray",
          color: "gray",
        }}
        className={`hierarchyTreeNode user-select-none ${clickable ? "link" : ""} ${
          inTariff.tariffCircuit.isDisabled && this.isHierarchyGrayOutEnabled ? "grayout" : ""
        }`}
        onClick={(event) => {
          if (!clickable) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }

          if (inIsRoot) {
            // select prevNode as root
            const newSelectedTcIds = () => {
              if (this.props.isHierarchyViewMode === true) {
                const tcModel = new TariffCircuitListModel(
                  this.props.tariffCircuits,
                  this.props.energyMeters,
                );

                return tcModel.updateHierarchySelection(
                  this.allTariffCircuits,
                  [inTariff.tariffCircuit.id],
                  undefined,
                );
              } else {
                const selectedList = this.allTariffCircuits
                  .filter(
                    (tc) =>
                      (tc.checked === HierarchySelectionState.SELECTED ||
                        tc.checked === HierarchySelectionState.SELECTED_CHILD) &&
                      inTariff.tariffCircuit.id !== tc.referenceEntry?.id,
                  )
                  .map((tc) => tc.referenceEntry?.id as number);

                if (inTariff.tariffCircuit.isDisabled === true) {
                  selectedList.push(inTariff.tariffCircuit.id);
                }
                return selectedList;
              }
            };

            this.props.onHierarchyChanged(
              newSelectedTcIds(),
              this.props.isHierarchyViewMode ?? true,
            );
            return;
          }

          // get children of this node and add them to additionalTCs
          const childIds =
            event.shiftKey ?
              this.openChilds(inTariff)
            : inTariff.childCircuits.map((child) => child.tariffCircuit.id);

          const additionalTCs = [
            ...(this.state.additionalTCs ?? this.additionalTCs).filter(
              (aTC) => !childIds.includes(aTC),
            ),
            ...childIds,
          ];

          this.setState({
            additionalTCs,
          });
        }}
        title={inTariff.tariffCircuit.tooltip ?? ""}
      >
        <div>{inIsRoot ? "^" : "+"}</div>
      </div>
    );
  }

  // scroll each TreeNode on first drawing into the middle of the view
  public componentDidMount(): void {
    const elements = document.getElementsByClassName("hierarchyTree");
    Array.from(elements).forEach((element) => {
      element.scrollLeft = element.scrollWidth / 2 - element.clientWidth / 2;
    });
  }

  public render() {
    // must be called in every derrived class from ChartBase!!!
    // the field allTariffCircuits is getting prepared within that function
    this.prepareHierarchyData();

    return this.renderTree();
  }

  private openChilds(aTariff: IHierarchyTreeNode): number[] {
    return aTariff.childCircuits.length > 0 ?
        [
          ...aTariff.childCircuits.map((child) => child.tariffCircuit.id),
          ...aTariff.childCircuits.flatMap((child) => this.openChilds(child)),
        ]
      : [];
  }

  private getParentIds(
    allTariffs: ITreeViewDropDownOption[],
    inTariffId?: number | null,
  ): number[] {
    return isSet(inTariffId) ?
        [
          ...this.getParentIds(
            allTariffs,
            allTariffs.find((tc) => tc.referenceEntry?.id === inTariffId)?.referenceEntry?.parent,
          ),
          inTariffId,
        ]
      : [];
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(HierarchyChartComponent);
